import { resolve, Promise } from 'rsvp';
import Service, { inject as service } from '@ember/service';
import config from 'sh-www/config/environment';

/**
 * The model types that we support to create Images.  Should be supported in the ImageController on the backend as well.
 *
 * @type {Object}
 */
export const IMAGE_COMMANDS__MODEL_TYPES = {
  PITCH: 'pitch',
  PROJECT_DIGEST: 'projectdigest'
};

/**
 * Service for project digest related API calls.
 */
export default Service.extend({
  errorTracker: service('error-tracker'),
  api: service(),
  store: service(),
  shData: service(),

  /**
   * Api key from cloudinary config.
   */
  get _cloudinaryApiKey() {
    return config.cloudinary.apiKey;
  },

  /**
   *
   */
  get cloudName() {
    return config.cloudinary.cloudName;
  },

  /**
   *
   * @param {function(Object): *} callback
   * @return {function(Response): Promise<*>}
   */
  unmarshallResponse(callback) {
    return (response) => {
      if (response.ok) {
        return response.json().then(callback);
      }

      this.get('errorTracker').error(`ImageCommands::unmarshallResponse - error uploading image statueCode: [${response.status}] statusText: [${response.statusText}]`);
      throw new Error(response.statusText);
    };
  },

  /**
   * @param {File} file
   * @returns {Promise<DS.Model>}
   */
  createDraftImage(file) {
    const options = {
      jsonData: {
        status: 'draft',
      },
    };

    // Create a draft image so we can get signing information
    return this.get('api').post('images', options).then((draftImage) => {
      const { timestamp, public_id, signature } = JSON.parse(draftImage.data.attributes['cloudinary-upload']);
      const { apiKey, cloudName } = config.cloudinary;

      const body = new FormData();
      body.append('file', file);
      body.append('timestamp', timestamp);
      body.append('public_id', public_id);
      body.append('api_key', apiKey);
      body.append('signature', signature);

      // Upload to cloudinary
      return fetch(`https://api.cloudinary.com/v1_1/${cloudName}/image/upload`, {
        body,
        method: 'POST',
      }).then(this.unmarshallResponse((cloudinaryResponse) => {
        const {
          width,
          height,
          format: imageType,
          version: cloudVersion,
          public_id: cloudId,
        } = cloudinaryResponse;

        // Update our own image with cloudinary data
        return this.get('api').put(`images/${draftImage.data.id}`, {
          jsonData: {
            width: cloudinaryResponse.width,
            height: cloudinaryResponse.height,
            type: `image/${cloudinaryResponse.format}`,
            cloud_version: cloudinaryResponse.version,
            cloud_id: cloudinaryResponse.public_id,
          }
        }).then((updatedDraftImage) => {
          const store = this.get('store');
          store.pushPayload('image', updatedDraftImage);
          return store.peekRecord('image', updatedDraftImage.data.id);
        })
      }));
    });
  },

  /**
   * @param {DS.Model|undefined|null} image
   * @param {DS.Model} associate
   * @returns {Promise<*>}
   */
  associateImage(image, associate) {
    return this.api.put(`images/${image.id}`, {
      jsonData: {
        imageable_id: associate.id,
        imageable_type: associate.constructor.modelName,
      },
    });
  },

  /**
   * @param {String} cloudVersion
   * @param {String} cloudID
   * @returns {String} The thumbnail URL.
   */
  cloudinaryThumbnailURL(cloudVersion, cloudID) {
    return `https://res.cloudinary.com/${this.get('cloudName')}/image/upload/w_800,h_800,c_limit,f_auto,q_auto/v${cloudVersion}/${cloudID}`;
  },

  /**
   * @param {String} cloudVersion
   * @param {String} cloudID
   * @param {String} transforms
   * @returns {String} The thumbnail URL.
   */
  cloudinaryThumbnailURLWithTransforms(cloudVersion, cloudID, transforms) {
    return `https://res.cloudinary.com/${this.cloudName}/image/upload/t_${transforms}/v${cloudVersion}/${cloudID}`;
  },

  /**
   * @param {string} version
   * @param {string} public_id
   * @returns {string}
   */
  cloudinaryProfileUrl(version, public_id) {
    return `https://res.cloudinary.com/${this.get('cloudName')}/image/upload/t_profile128,f_auto,q_auto/v${version}/${public_id}`;
  },

  /**
   * Generate a signature to do a Cloudinary image upload from the client.
   *
   * @param {String} modelType    of type IMAGE_COMMANDS__MODEL_TYPES.*
   * @return {Ember.RSVP.Promise}
   */
  getCloudinarySignatureForModel(modelType) {
    return new Promise((resolve, reject) => {

      let options = {
        jsonData: {
          model_type: modelType
        }
      };

      this.get('api').post('images/signature', options).then(response => {
        const {timestamp, public_id, signature} = response.data;
        const api_key = this.get('_cloudinaryApiKey');
        const cloudinaryData = {
          public_id,
          timestamp,
          signature,
          api_key
        };

        resolve(cloudinaryData);
      }).catch(e => {
        this.get('errorTracker').error(`getCloudinarySignatureForModel -- Could not get signature for ${modelType}`, this, e);
        reject(e);
      });
    });
  },

  /**
   * Save a new image and associate it with a particular model.
   *
   * @param {String} modelType    IMAGE_COMMANDS__MODEL_TYPES.*
   * @param {String} modelId      Id of the model we will associate with.
   * @param {Number} width
   * @param {Number} height
   * @param {String} cloudId      Should be set from the Cloudinary bag after the upload
   * @param {String} cloudVersion Should be set from the Cloudinary bag after the upload
   * @param {String} imageType    Should be the image type (format) from Cloudinary
   * @param {String=} opt_include
   * @return {Ember.RSVP.Promise}
   */
  saveNewImageForModel(modelType, modelId, width, height, cloudId, cloudVersion, imageType, opt_include) {
    return new Promise((resolve, reject) => {

      let options = {
        jsonData: {
          imageable_type: modelType,
          imageable_id: modelId,
          width: width,
          height: height,
          type: imageType,
          cloud_id: cloudId,
          cloud_version: cloudVersion
        },
        params : {
          include: ''
        }
      };

      if (opt_include) {
        options.params.include = opt_include;
      }

      this.get('api').post('images', options).then(response => {
        this.shData.pushResponseToStore(response);

        let newImage = this.get('store').peekRecord('image', response.data.id);
        resolve(newImage);
      }).catch(e => {
        this.get('errorTracker').error(`saveNewImageForModel -- Could not save image for modelType:${modelType} and modelId:${modelId}`, this, e);
        reject(e);
      });
    });
  }
});
